/**
 * How much of a result is on the screen, and how to reach the rest.
 *
 * Three screens listed truncated results and none of them said so. Events rendered the newest 50 of
 * 6,426 with no count and no control — the server returns `total` and `hasMore` on every page and the
 * component read only `.items`. Explore printed the true total and then showed 100 rows with no way to
 * reach the 101st, though its query has always accepted an offset. The alert timeline showed 100
 * occurrences under a header saying 149.
 *
 * Shared because the sentence is the same on all three, and because the failure they had in common was
 * not a missing control but a missing statement: an operator who cannot see that a list is cut cannot
 * know to narrow it. Stating it is the part that matters; paging is the convenience.
 */

export interface PageState {
  /** The sentence under the table. Always present — a complete result says so too. */
  summary: string
  /** True when there is anything above this page. */
  canGoBack: boolean
  /** True when the server said there is more after this page. */
  canGoForward: boolean
  /** 1-based, for the summary. */
  firstRow: number
  lastRow: number
  truncated: boolean
}

/**
 * The noun the sentence is about, singular and plural.
 *
 * Parameterized rather than hard-coded to "event" because the Devices screen had the same defect and
 * could not use this module to fix it: it rendered fifty of nine hundred and eighty-seven rows and
 * said nothing, which is the exact sentence this file exists to make somebody write. A default keeps
 * the two existing callers reading as they did.
 */
export interface Noun {
  one: string
  many: string
}

const EVENTS: Noun = { one: 'event', many: 'events' }

export function pageState(total: number, offset: number, shown: number, noun: Noun = EVENTS): PageState {
  const firstRow = total === 0 ? 0 : offset + 1
  const lastRow = offset + shown
  const truncated = total > shown

  return {
    summary: describe(total, firstRow, lastRow, truncated, noun),
    canGoBack: offset > 0,
    canGoForward: lastRow < total,
    firstRow,
    lastRow,
    truncated
  }
}

function describe(total: number, firstRow: number, lastRow: number, truncated: boolean, noun: Noun): string {
  if (total === 0) return `No matching ${noun.many}.`

  const count = `${format(total)} ${total === 1 ? noun.one : noun.many}`
  if (!truncated) return `${count}.`

  return `Showing ${format(firstRow)}–${format(lastRow)} of ${count}.`
}

const FULL = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 })
const format = (value: number): string => FULL.format(value)

/** The offset for the next page, clamped so it can never run past the end. */
export function nextOffset(total: number, offset: number, limit: number): number {
  return Math.min(offset + limit, Math.max(0, total - 1))
}

/** The offset for the previous page, clamped at zero. */
export function previousOffset(offset: number, limit: number): number {
  return Math.max(0, offset - limit)
}
